/**
 * AvatarUploadModal — web replacement for the mobile image picker sheet.
 * The picked File rides to the API as `avatarBlob`; browsers reject the
 * `{ uri, name, type }` descriptor React Native puts in FormData.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { Button } from '../../components/ui/Button';
import { CameraIcon } from '../../components/ui/icons';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import './profile.css';

interface AvatarUploadModalProps {
  currentAvatarUrl: string;
  onClose: () => void;
}

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

export function AvatarUploadModal({ currentAvatarUrl, onClose }: AvatarUploadModalProps) {
  const { t } = useTranslation();
  const { updateProfile } = useProfile();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const objectUrlRef = useRef('');

  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const handleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;

    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setError(t('profile.avatarUpload.invalidType'));
      return;
    }
    if (selected.size > MAX_BYTES) {
      setError(t('profile.avatarUpload.tooLarge'));
      return;
    }

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = URL.createObjectURL(selected);
    setError('');
    setFile(selected);
    setPreviewUrl(objectUrlRef.current);
  };

  const handleSave = async () => {
    if (!file) return;
    setSaving(true);
    try {
      await updateProfile({
        avatarBlob: file,
      });
      ToastService.show({ type: 'success', message: t('profile.updateProfile.saved') });
      onClose();
    } catch {
      setError(t('profile.avatarUpload.failed'));
    } finally {
      setSaving(false);
    }
  };

  const shownUrl = previewUrl || currentAvatarUrl;

  return (
    <Modal
      title={t('profile.avatarUpload.title')}
      labelledBy="avatar-upload-title"
      onClose={onClose}
    >
      <div className="avatar-upload">
        <div className="avatar-upload__preview">
          {shownUrl ? <img src={shownUrl} alt="" aria-hidden /> : <CameraIcon size={32} />}
        </div>

        <label className="avatar-upload__picker">
          <CameraIcon size={16} />
          {t(file ? 'profile.avatarUpload.replace' : 'profile.avatarUpload.choose')}
          <input
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleSelect}
            disabled={saving}
          />
        </label>

        <p className="avatar-upload__hint">{t('profile.avatarUpload.hint')}</p>
        <FormError variant="banner" message={error} />

        <div className="avatar-upload__actions">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            onClick={() => { handleSave().catch(() => undefined); }}
            disabled={!file || saving}
          >
            {t(saving ? 'profile.avatarUpload.saving' : 'profile.avatarUpload.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
